import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Edit2, Eye, Trash2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LockerAlerts = ({ token, clients }) => {
  const [alerts, setAlerts] = useState([]);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    tipoFecha: 'creacion',
    estatus: 'por-procesar',
    desde: '',
    hasta: '',
    tipoEnvio: 'todos',
    agencia: 'todas'
  });

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/admin/alerts`, axiosConfig);
      setAlerts(response.data);
    } catch (error) {
    }
  };

  const getClientInfo = (clientId) => {
    return clients.find(c => c.id === clientId) || {};
  };

  const handleSelectAll = () => {
    if (selectedAlerts.length === filteredAlerts.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(filteredAlerts.map(a => a.id));
    }
  };

  const toggleSelectAlert = (alertId) => {
    if (selectedAlerts.includes(alertId)) {
      setSelectedAlerts(selectedAlerts.filter(id => id !== alertId));
    } else {
      setSelectedAlerts([...selectedAlerts, alertId]);
    }
  };

  const handleProcessAlert = async (alertId) => {
    if (!window.confirm('¿Procesar esta alerta y crear el tracking?')) return;
    
    try {
      await axios.put(`${API}/admin/alerts/${alertId}/process`, {}, axiosConfig);
      alert('✅ Alerta procesada y tracking creado');
      fetchAlerts();
    } catch (error) {
      alert('Error al procesar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const client = getClientInfo(alert.client_id);
    const matchesSearch = !filters.search || 
      alert.tracking_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
      client.full_name?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = filters.estatus === 'todos' ||
      (filters.estatus === 'por-procesar' && alert.status === 'pending') ||
      (filters.estatus === 'procesado' && alert.status === 'processed');
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <span className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold">POR PROCESAR</span>;
      case 'acknowledged':
        return <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold">RECONOCIDO</span>;
      case 'processed':
        return <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">PROCESADO</span>;
      default:
        return <span className="bg-gray-500 text-white px-2 py-1 rounded text-xs font-semibold">{status}</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border-b pb-4">
        <h1 className="text-2xl font-light text-gray-700 mb-4">Lista de Alertas</h1>
        <nav className="flex text-sm text-gray-500">
          <span className="text-blue-600 hover:underline cursor-pointer">Home</span>
          <span className="mx-2">/</span>
          <span>Lista de Alertas</span>
        </nav>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-sm text-gray-600">Búsqueda</Label>
              <Input
                placeholder="Número,Casillero,Destinatario,Tracking"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Tipo fecha</Label>
              <Select value={filters.tipoFecha} onValueChange={(v) => setFilters({...filters, tipoFecha: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="creacion">Creación</SelectItem>
                  <SelectItem value="llegada">Llegada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Estatus</Label>
              <Select value={filters.estatus} onValueChange={(v) => setFilters({...filters, estatus: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="por-procesar">Por Procesar</SelectItem>
                  <SelectItem value="procesado">Procesado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Desde</Label>
              <Input
                type="date"
                value={filters.desde}
                onChange={(e) => setFilters({...filters, desde: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-gray-600">Hasta</Label>
              <Input
                type="date"
                value={filters.hasta}
                onChange={(e) => setFilters({...filters, hasta: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Tipo de envío</Label>
              <Select value={filters.tipoEnvio} onValueChange={(v) => setFilters({...filters, tipoEnvio: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aereo">Aéreo</SelectItem>
                  <SelectItem value="maritimo">Marítimo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Agencia</Label>
              <Select value={filters.agencia} onValueChange={(v) => setFilters({...filters, agencia: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="traetelodafuera">Traetelodafuera</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date Display */}
      <div className="text-sm text-gray-600">
        {new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleSelectAll}
        >
          {selectedAlerts.length === filteredAlerts.length ? 'Ninguna' : 'Todas'}
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            if (selectedAlerts.length === 0) {
              alert('Selecciona al menos una alerta');
              return;
            }
            const action = window.prompt('Acciones disponibles:\n1. Marcar como leídas\n2. Eliminar\n\nEscribe el número de la acción:');
            if (action === '1') {
              alert('Funcionalidad de marcar como leídas en desarrollo');
            } else if (action === '2') {
              if (window.confirm(`¿Eliminar ${selectedAlerts.length} alerta(s)?`)) {
                alert('Funcionalidad de eliminación en desarrollo');
              }
            }
          }}
        >
          Acciones en lote
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => alert('Función de reportes en desarrollo. Podrás exportar a Excel/PDF las alertas filtradas.')}
        >
          Reportes
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Acción</th>
                <th className="text-left p-3 font-semibold text-gray-700">Número</th>
                <th className="text-left p-3 font-semibold text-gray-700">Casillero</th>
                <th className="text-left p-3 font-semibold text-gray-700">Creado</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tracking</th>
                <th className="text-left p-3 font-semibold text-gray-700">Destinatario</th>
                <th className="text-left p-3 font-semibold text-gray-700">Envío</th>
                <th className="text-left p-3 font-semibold text-gray-700">Llega</th>
                <th className="text-left p-3 font-semibold text-gray-700">Piezas</th>
                <th className="text-left p-3 font-semibold text-gray-700">Peso</th>
                <th className="text-left p-3 font-semibold text-gray-700">Descripción</th>
                <th className="text-left p-3 font-semibold text-gray-700">Agencia</th>
                <th className="text-left p-3 font-semibold text-gray-700">Estatus</th>
                <th className="text-left p-3 font-semibold text-gray-700">Monto Declarado</th>
                <th className="text-left p-3 font-semibold text-gray-700">Carrier</th>
                <th className="text-left p-3 font-semibold text-gray-700">Seguro</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((alert) => {
                const client = getClientInfo(alert.client_id);
                return (
                  <tr key={alert.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedAlerts.includes(alert.id)}
                          onChange={() => toggleSelectAlert(alert.id)}
                          className="w-4 h-4"
                        />
                        <button className="text-blue-500 hover:text-blue-700">
                          <Eye size={16} />
                        </button>
                        <button className="text-green-500 hover:text-green-700">
                          <Edit2 size={16} />
                        </button>
                        <button className="text-red-500 hover:text-red-700">
                          <Trash2 size={16} />
                        </button>
                        <Button
                          size="sm"
                          onClick={() => handleProcessAlert(alert.id)}
                          className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
                          disabled={alert.status === 'processed'}
                        >
                          Procesar
                        </Button>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="inline-block bg-green-500 text-white px-3 py-1 rounded text-xs font-semibold">
                        ALERT{alert.id.substring(0, 3).toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="inline-block bg-green-500 text-white px-3 py-1 rounded text-xs font-semibold">
                        {client.locker_id || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-600 text-xs">
                      {new Date(alert.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="p-3 text-gray-600">
                      {alert.tracking_number}
                    </td>
                    <td className="p-3 text-gray-600">
                      {client.full_name || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600 text-xs">
                      {new Date(alert.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="p-3 text-gray-600 text-xs">
                      {alert.estimated_arrival_date 
                        ? new Date(alert.estimated_arrival_date).toLocaleDateString('es-ES')
                        : 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600 text-center">1</td>
                    <td className="p-3 text-gray-600">
                      {alert.estimated_weight || '0.00'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {alert.description || alert.store_origin || 'N/A'}
                    </td>
                    <td className="p-3 text-gray-600 text-xs">
                      Traetelodafuera_miami
                    </td>
                    <td className="p-3">
                      {getStatusBadge(alert.status)}
                    </td>
                    <td className="p-3 text-gray-600">
                      ${alert.declared_value || '0.00'}
                    </td>
                    <td className="p-3 text-gray-600">
                      {alert.carrier || 'Amazon'}
                    </td>
                    <td className="p-3 text-gray-600 text-center">
                      {alert.insurance ? 'NO RECIBIDA' : '0.00'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAlerts.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron alertas
          </div>
        )}
      </div>
    </div>
  );
};

export default LockerAlerts;
